import type {VoidFunctionComponent} from "react";
import React, {useState} from "react";
import s from "./RadioGroupDocumentation.module.scss";
import RadioGroup, {IRadioGroupOption} from "../../../components/common/RadioGroup/RadioGroup";
import {Example} from "../Example";
import {ComponentDescription} from "../documentationTools/ComponentDescription";
import {Property} from "../documentationTools/Property";
import {PropertyListHeader} from "../documentationTools/PropertyListHeader";
import {IPropertyWrapper} from "../documentationTools/DocumentationTypes";
import {sortProperties} from "../documentationTools/documentation-utility";
import {FontIcon} from "../../../components/common/FontIcon";

export interface IRadioGroupDocumentationProps {
}

// Define options outside component to prevent recreating on every render
const basicOptions: IRadioGroupOption[] = [
  {value: "option1", label: "Option 1"},
  {value: "option2", label: "Option 2"},
  {value: "option3", label: "Option 3"},
];

const sizeOptions: IRadioGroupOption[] = [
  {value: "small", label: "Small"},
  {value: "medium", label: "Medium"},
  {value: "large", label: "Large"},
];

const fruitOptions: IRadioGroupOption[] = [
  {value: "apple", label: "Apple"},
  {value: "banana", label: "Banana"},
  {value: "orange", label: "Orange"},
  {value: "grape", label: "Grape"},
];

const directionOptions: IRadioGroupOption[] = [
  {value: "auto", label: "Auto"},
  {value: "vertical", label: <FontIcon icon={"icon-arrow-down"}/>},
  {value: "horizontal", label: <FontIcon icon={"icon-arrow-right"}/>},
];

export const RadioGroupDocumentation: VoidFunctionComponent<IRadioGroupDocumentationProps> = ({}) => {
  let propertyIndex = 0;

  // State hooks for interactive examples
  const [ordinal, setOrdinal] = useState<string>("option1");
  const [size, setSize] = useState<string>("medium");
  const [fruit, setFruit] = useState<string>("apple");
  const [selectedDirection, setSelectedDirection] = useState<string>("auto");

  const properties: IPropertyWrapper[] = [
    {
      isRequired: true,
      name: "options",
      element: <Property
        key={++propertyIndex}
        name={"options"}
        description={"Array of options to display. Each option has a value (string) and label (string or ReactNode)."}
        isRequired={true}
        type={"IRadioGroupOption<T>[]"}
        examples={<>
          <Example title={'options with string labels'}>
            <RadioGroup
              options={basicOptions}
              value={ordinal}
              onChange={(value) => setOrdinal(value)}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: true,
      name: "value",
      element: <Property
        key={++propertyIndex}
        name={"value"}
        description={"The currently selected value. Must match one of the option values."}
        isRequired={true}
        type={"T (extends string)"}
        examples={<>
          <Example title={'value controls which option is selected'}>
            <RadioGroup
              options={sizeOptions}
              value={size}
              onChange={(value) => setSize(value)}
            />
            <div className={s.exampleNote}>Currently selected: {size}</div>
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "onChange",
      element: <Property
        key={++propertyIndex}
        name={"onChange"}
        description={"Callback function called when a new option is selected. Receives the selected value as a parameter."}
        isRequired={false}
        type={"(value: T) => void"}
        examples={<>
          <Example title={'onChange handler'}>
            <RadioGroup
              options={fruitOptions}
              value={fruit}
              onChange={(value) => {
                setFruit(value);
                alert(`You selected: ${value}`);
              }}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "label",
      element: <Property
        key={++propertyIndex}
        name={"label"}
        description={"Optional label displayed above the option group."}
        isRequired={false}
        type={"string"}
        examples={<>
          <Example title={'With label'}>
            <RadioGroup
              options={basicOptions}
              value={ordinal}
              label="Choose an Option"
              onChange={(value) => setOrdinal(value)}
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "type",
      element: <Property
        key={++propertyIndex}
        name={"type"}
        description={<>
          Controls the visual appearance of the radio group. &quot;radios&quot; shows traditional radio buttons, &quot;checkmarks&quot;
          shows checkmark icons (vertical) or highlighted backgrounds (horizontal), and &quot;buttons&quot; shows a compact
          button group style.
        </>}
        isRequired={false}
        type={'"radios" | "buttons" | "checkmarks"'}
        defaultValue={'"radios"'}
        examples={<>
          <Example title={'type = "radios"'}>
            <RadioGroup
              options={basicOptions}
              value={ordinal}
              type="radios"
              onChange={(value) => setOrdinal(value)}
            />
          </Example>
          <br/>
          <Example title={'type = "checkmarks"'}>
            <RadioGroup
              options={basicOptions}
              value={ordinal}
              type="checkmarks"
              onChange={(value) => setOrdinal(value)}
            />
          </Example>
          <br/>
          <Example title={'type = "buttons" with text labels'}>
            <RadioGroup
              options={basicOptions}
              value={ordinal}
              type="buttons"
              onChange={(value) => setOrdinal(value)}
            />
          </Example>
          <br/>
          <Example title={'type = "buttons" with ReactNode labels (FontIcon components)'}>
            <RadioGroup
              options={directionOptions}
              value={selectedDirection}
              onChange={(value) => setSelectedDirection(value)}
              type="buttons"
              label="Direction"
            />
          </Example>
        </>}
      />
    },

    {
      isRequired: false,
      name: "additionalClassName",
      element: <Property
        key={++propertyIndex}
        name={"additionalClassName"}
        description={"Allows passing in additional CSS class names for custom styling."}
        isRequired={false}
        type={"string"}
        defaultValue={'""'}
        examples={<>
          <Example
            title={`additionalClassName = "${s.customRadioGroup}" (see custom styles in RadioGroupDocumentation.module.scss)`}>
            <RadioGroup
              options={basicOptions}
              value={ordinal}
              onChange={(value) => setOrdinal(value)}
              additionalClassName={s.customRadioGroup}
            />
          </Example>
        </>}
      />
    },
  ];

  sortProperties(properties);

  return <div className={s.container}>
    <ComponentDescription
      name={"RadioGroup"}
      description={"A group of buttons, of which only one can be selected simultaneously. Provides three visual types (radios, buttons, checkmarks) and supports both horizontal and vertical layouts."}
    />

    <PropertyListHeader properties={properties}/>

    {properties.map((property, index) => {
      return <React.Fragment key={index}>
        {property.element}
        {index < properties.length - 1 && <hr className={s.propertyDivider}/>}
      </React.Fragment>
    })}

  </div>;
}

